/**
 * 사내결재 문서함 — [분기 재고 실사] 탭 (v2.224.0 · 2026-10-01)
 *
 * ⭐이 탭에는 **작성 단추가 없다.** 보고서는 홈 화면 앱(📦 재고 › 분기 재고 실사)의 마침 화면에서 실사 담당자가
 *   [📤 보고서 상신]으로 올린다. 여기서 하는 일은 「보고 · 결재」뿐이다(영업팀 월간보고서 탭과 같은 모양).
 * ⭐결재선 3칸 — 담당(실사 담당) → 부서장 → 대표이사. 경영지원 결재 없음(서버 NO_SUPPORT_STAGE).
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import * as api from '../../lib/expense-api';
import { EXPENSE_STATUS_BADGE } from '../../components/ExpenseDocModal';
import { StockAuditReportModal } from '../../components/StockAuditReportModal';
import { openServerPdfPreview } from '../../lib/pdf-preview';
import { LoadingLines } from '../../components/ui/Skeleton';

const YEARS = Array.from({ length: 4 }, (_, i) => new Date().getFullYear() - i);

/** 제목 「2026년 3분기 재고 실사 보고서」에서 분기만 — 형식이 다르면 빈칸 */
function quarterFromTitle(title: string | null): string {
  const m = /(\d{4})년\s*(\d)분기/.exec(title ?? '');
  return m ? `${m[1]}년 ${m[2]}분기` : '';
}

export function StockAuditReportTab() {
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState<number | null>(null);

  const finalQuery = useQuery({
    queryKey: ['expense', 'docs', 'stockAudit', 'final', year, q],
    queryFn: () => api.listExpenseDocs({ group: 'stockAudit', final: true, year, q: q.trim() || undefined }),
    staleTime: 15_000,
  });
  const openQuery = useQuery({
    queryKey: ['expense', 'docs', 'stockAudit', 'open'],
    queryFn: () => api.listExpenseDocs({ group: 'stockAudit', notFinal: true }),
    staleTime: 15_000,
  });
  const pending = openQuery.data?.rows ?? [];
  const rows = finalQuery.data?.rows ?? [];

  return (
    <div className="space-y-5">
      <div className="bg-blue-50 border border-blue-200 rounded p-4 text-sm text-blue-900">
        <div className="font-semibold mb-1">분기 재고 실사 보고서</div>
        <ul className="list-disc pl-5 space-y-0.5 text-[13px] text-blue-800">
          <li>작성은 홈 화면 앱 <b>📦 재고 › 🗓 분기 재고 실사</b>의 마침 화면에서 실사 담당자가 [📤 보고서 상신]으로 합니다 — 이 화면에서는 만들 수 없습니다.</li>
          <li>결재선은 <b>3칸</b>입니다 — 담당(실사 담당) → 부서장 → 대표이사. <b>경영지원 결재는 없습니다.</b></li>
          <li><b>숫자는 상신 시점으로 굳습니다</b> — 반려되면 담당자가 품목 판정·사진을 고쳐 다시 상신하며 그때 다시 굳습니다.</li>
          <li>품목 하나하나와 사진은 <Link to="/stock?tab=audit" className="underline">재고관리 › 분기 실사</Link> 탭에서 봅니다(사진은 NAS 보관 · PDF 에는 싣지 않습니다).</li>
          <li>진행 중인 결재는 <Link to="/approvals" className="underline">내 결재함</Link>에서도 처리할 수 있습니다.</li>
        </ul>
      </div>

      {pending.length > 0 && (
        <section>
          <h3 className="ui-subsection-title mb-2">진행 중 <span className="text-slate-400 font-normal">{pending.length}건</span></h3>
          <div className="border border-slate-200 rounded overflow-hidden">
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2 font-medium w-[132px]">문서번호</th>
                  <th className="text-left px-3 py-2 font-medium w-[104px]">상태</th>
                  <th className="text-left px-3 py-2 font-medium w-[120px]">분기</th>
                  <th className="text-left px-3 py-2 font-medium w-[92px]">작성자</th>
                  <th className="text-left px-3 py-2 font-medium">제목</th>
                </tr>
              </thead>
              <tbody>
                {pending.map((r) => (
                  <tr key={r.id} onClick={() => setOpenId(r.id)} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer">
                    <td className="px-3 py-2 font-mono text-xs">{r.docNo}</td>
                    <td className="px-3 py-2"><span className={`text-xs px-1.5 py-0.5 rounded ${EXPENSE_STATUS_BADGE[r.status] ?? ''}`}>{r.status}</span></td>
                    <td className="px-3 py-2 text-slate-600">{quarterFromTitle(r.purpose)}</td>
                    <td className="px-3 py-2">{r.authorName}</td>
                    <td className="px-3 py-2 truncate">{r.purpose ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section>
        <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
          <h3 className="ui-subsection-title">결재완료 <span className="text-slate-400 font-normal">{rows.length}건</span></h3>
          <div className="flex items-center gap-2">
            <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="ui-input">
              {YEARS.map((y) => <option key={y} value={y}>{y}년</option>)}
            </select>
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="문서번호·작성자·제목 검색…" className="ui-input w-60" />
          </div>
        </div>
        <div className="border border-slate-200 rounded overflow-hidden">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-3 py-2 font-medium w-[132px]">문서번호</th>
                <th className="text-left px-3 py-2 font-medium w-[120px]">분기</th>
                <th className="text-left px-3 py-2 font-medium w-[110px]">작성일</th>
                <th className="text-left px-3 py-2 font-medium w-[92px]">작성자</th>
                <th className="text-left px-3 py-2 font-medium">제목</th>
                <th className="px-3 py-2 font-medium w-[52px]"></th>
              </tr>
            </thead>
            <tbody>
              {finalQuery.isLoading ? (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400"><LoadingLines /></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400">결재완료된 분기 재고 실사 보고서가 없습니다.</td></tr>
              ) : rows.map((r) => (
                <tr key={r.id} onClick={() => setOpenId(r.id)} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer">
                  <td className="px-3 py-2 font-mono text-xs">{r.docNo}</td>
                  <td className="px-3 py-2 text-slate-600">{quarterFromTitle(r.purpose)}</td>
                  <td className="px-3 py-2 text-slate-600">{r.docDate}</td>
                  <td className="px-3 py-2">{r.authorName}</td>
                  <td className="px-3 py-2 truncate">{r.purpose ?? ''}</td>
                  <td className="px-3 py-2 text-center">
                    {r.attachmentId && (
                      <button type="button" title="결재본 보기"
                        onClick={(e) => { e.stopPropagation(); openServerPdfPreview({ pdfUrl: api.attachmentDownloadUrl(r.attachmentId!), filename: `${r.docNo}_분기재고실사보고서.pdf` }); }}
                        className="text-slate-400 hover:text-blue-600">📄</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {openId !== null && <StockAuditReportModal docId={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}
